import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BpsGlobalLogo from '../Logo/BpsGlobal.jpeg';

// Helper function to load image as base64
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

export const generatePackingSlip = async (saleData, companyDetails, items, productRelationDetails) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // BPS Global Trading color scheme - matching logo
  const bpsBlue = [0, 102, 204]; // Primary blue from logo
  const bpsDarkBlue = [0, 70, 150]; // Darker blue for gradient
  const bpsGreen = [34, 197, 94]; // Green accent for packing slip
  const darkGray = [52, 58, 64];

  // Header with white background
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Add a subtle border at the bottom of header
  doc.setDrawColor(...bpsBlue);
  doc.setLineWidth(0.5);
  doc.line(0, 45, pageWidth, 45);

  // Company Logo - Load and add BpsGlobal.jpeg
  try {
    const logoBase64 = await loadImageAsBase64(BpsGlobalLogo);
    doc.addImage(logoBase64, 'JPEG', 15, 10, 25, 25);
  } catch (error) {
    console.error('Error loading logo:', error);
    // Fallback to placeholder if logo fails to load
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(15, 10, 25, 25, 3, 3, 'F');
    doc.setFontSize(16);
    doc.setTextColor(0, 102, 204);
    doc.setFont('helvetica', 'bold');
    doc.text('LOGO', 20, 25);
  }

  // Company Name and Details
  doc.setFontSize(22);
  doc.setTextColor(...bpsBlue);
  doc.setFont('helvetica', 'bold');
  doc.text(companyDetails.companyName || 'BPS Global Trading', 45, 20);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkGray);
  doc.text(companyDetails.address || 'Dubai, United Arab Emirates', 45, 27);
  doc.text(`TRN: ${companyDetails.trn || companyDetails.vatNumber || 'N/A'}`, 45, 32);
  doc.text(`Tel: ${companyDetails.phone || '+971-XX-XXXXXXX'} | Email: ${companyDetails.email || 'info@company.ae'}`, 45, 37);

  // Packing Slip Text (no badge background)
  doc.setFontSize(14);
  doc.setTextColor(...bpsBlue);
  doc.setFont('helvetica', 'bold');
  doc.text('PACKING SLIP', pageWidth - 57.5, 18);

  // Details Box
  const detailsBoxY = 50;
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(15, detailsBoxY, pageWidth - 30, 35, 2, 2, 'F');
  doc.setDrawColor(...bpsBlue);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, detailsBoxY, pageWidth - 30, 35, 2, 2, 'S');

  // Left side - Customer Details
  doc.setFontSize(10);
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'bold');
  doc.text('SHIP TO:', 20, detailsBoxY + 7);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...bpsBlue);
  doc.text(saleData.CustomerName || 'Customer Name', 20, detailsBoxY + 13);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  const customerAddress = saleData.ShipToAddress1 || 'Customer Address';
  doc.text(customerAddress.substring(0, 40), 20, detailsBoxY + 18);
  if (saleData.ShipToAddress2) {
    doc.text(saleData.ShipToAddress2.substring(0, 40), 20, detailsBoxY + 23);
  }
  doc.text(`TRN: ${saleData.VATNumber || 'N/A'}`, 20, detailsBoxY + 28);

  // Right side - Packing Slip Info
  const rightX = pageWidth - 70;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  
  doc.text('Invoice No:', rightX, detailsBoxY + 7);
  doc.text('Invoice Date:', rightX, detailsBoxY + 13);
  doc.text('Salesman:', rightX, detailsBoxY + 19);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...bpsBlue);
  const billNo = saleData.BillPrefix && saleData.FinYear 
    ? `${saleData.BillPrefix}/${saleData.BillNumber}/${saleData.FinYear}`
    : saleData.BillNumber || 'N/A';
  doc.text(billNo, rightX + 30, detailsBoxY + 7);
  
  const billDate = saleData.BillDate 
    ? new Date(saleData.BillDate).toLocaleDateString('en-GB')
    : 'N/A';
  doc.text(billDate, rightX + 30, detailsBoxY + 13);
  doc.text(saleData.SalesmanName || 'N/A', rightX + 30, detailsBoxY + 19);

  // Product Relation Details Table
  const tableStartY = detailsBoxY + 42;
  
  // Group items by ProductItemId
  const groupedItems = {};
  if (productRelationDetails && productRelationDetails.length > 0) {
    productRelationDetails.forEach(detail => {
      const productId = detail.ProductItemId;
      if (!groupedItems[productId]) {
        groupedItems[productId] = {
          productName: items.find(i => i.Id === productId)?.Name || `Product ${productId}`,
          items: []
        };
      }
      groupedItems[productId].items.push(detail);
    });
  }

  const tableData = [];
  let serialNo = 1;

  Object.keys(groupedItems).forEach(productId => {
    const group = groupedItems[productId];
    
    // Add product header row
    tableData.push([
      { content: group.productName, colSpan: 4, styles: { fontStyle: 'bold', fillColor: [220, 252, 231], textColor: [22, 101, 52] } }
    ]);
    
    // Add related items
    group.items.forEach(item => {
      const itemName = items.find(i => i.Id === item.ItemId)?.Name || 'Item';
      const hsnCode = items.find(i => i.Id === item.ItemId)?.HSNCode || '-';
      
      tableData.push([
        serialNo++,
        itemName,
        hsnCode,
        parseFloat(item.Quantity || 0).toFixed(2)
      ]);
    });
  });

  // If no product relation details, show sale details
  if (tableData.length === 0 && saleData.saleDetails && saleData.saleDetails.length > 0) {
    saleData.saleDetails.forEach((item, index) => {
      const itemName = items.find(i => i.Id === item.ItemId)?.Name || 'Item';
      tableData.push([
        index + 1,
        itemName,
        item.HSNCode || '-',
        parseFloat(item.Quantity || 0).toFixed(2)
      ]);
    });
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [[
      '#',
      'Item Description',
      'HSN Code',
      'Quantity'
    ]],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: bpsBlue,
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      lineWidth: 0.1,
      lineColor: [255, 255, 255]
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [52, 58, 64],
      lineWidth: 0.1,
      lineColor: [200, 200, 200]
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 15 },
      1: { halign: 'left' },
      2: { halign: 'center', cellWidth: 30 },
      3: { halign: 'center', cellWidth: 30 }
    },
    alternateRowStyles: {
      fillColor: [248, 249, 250]
    },
    margin: { left: 15, right: 15 }
  });

  // Notes Section
  const notesY = doc.lastAutoTable.finalY + 15;
  doc.setFillColor(248, 249, 250);
  doc.rect(15, notesY, pageWidth - 30, 30, 'F');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Notes:', 20, notesY + 8);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('1. Please check all items carefully upon receipt.', 20, notesY + 14);
  doc.text('2. Report any discrepancies within 24 hours.', 20, notesY + 19);
  doc.text('3. This is a packing slip and not a tax invoice.', 20, notesY + 24);

  // Footer
  const footerY = pageHeight - 25;
  doc.setDrawColor(...bpsBlue);
  doc.setLineWidth(0.5);
  doc.line(15, footerY, pageWidth - 15, footerY);

  // Signature section
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Packed By', 20, footerY + 10);
  doc.text('Received By', pageWidth - 60, footerY + 10);

  // Footer text
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('This is a computer-generated packing slip.', pageWidth / 2, footerY + 18, { align: 'center' });

  return doc;
};

export const printPackingSlip = async (saleData, companyDetails, items, productRelationDetails) => {
  const doc = await generatePackingSlip(saleData, companyDetails, items, productRelationDetails);
  
  // Method 1: Try using autoPrint
  doc.autoPrint();
  
  // Open in new window
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  
  const printWindow = window.open(pdfUrl, '_blank');
  
  if (printWindow) {
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
    
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 10000);
  } else {
    // Fallback: download the PDF
    const billNo = saleData.BillPrefix && saleData.FinYear 
      ? `${saleData.BillPrefix}_${saleData.BillNumber}_${saleData.FinYear}`
      : `PackingSlip_${saleData.BillNumber}`;
    doc.save(`${billNo}_PackingSlip.pdf`);
    URL.revokeObjectURL(pdfUrl);
  }
};

export const downloadPackingSlip = async (saleData, companyDetails, items, productRelationDetails) => {
  const doc = await generatePackingSlip(saleData, companyDetails, items, productRelationDetails);
  const billNo = saleData.BillPrefix && saleData.FinYear 
    ? `${saleData.BillPrefix}_${saleData.BillNumber}_${saleData.FinYear}`
    : `PackingSlip_${saleData.BillNumber}`;
  doc.save(`${billNo}_PackingSlip.pdf`);
};
